import React from 'react';
import { Box, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import {
  guaranteesClientText,
  normalizeGuaranteesTenderIdForBackend,
} from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import { validateGuaranteesTenderPublicId } from '../../../rootSection/utils.ts';
import type { TGuaranteesRootSectionFormValues } from '../../../rootSection/types.ts';

interface ITenderSearchRowProps {
  disabled: boolean;
  isSearchingTender: boolean;
  isSelectingTender: boolean;
  onTenderSearch: (tenderPublicId: string) => Promise<void>;
}

const TenderSearchRow: React.FC<ITenderSearchRowProps> = ({
  disabled,
  isSearchingTender,
  isSelectingTender,
  onTenderSearch,
}) => {
  const { getValues, setValue } = useFormContext<TGuaranteesRootSectionFormValues>();
  const [tenderSearchError, setTenderSearchError] = React.useState('');

  const handleTenderSearchChange = () => {
    if (tenderSearchError) {
      setTenderSearchError('');
    }
  };

  const handleTenderSearchClick = () => {
    const normalizedTenderId = normalizeGuaranteesTenderIdForBackend(
      String(getValues('tenderSearch') ?? '')
    );
    const validationError = validateGuaranteesTenderPublicId(normalizedTenderId);

    setValue('tenderSearch', normalizedTenderId, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setTenderSearchError(validationError);

    if (validationError) {
      return;
    }

    void onTenderSearch(normalizedTenderId);
  };

  return (
    <Box sx={guaranteesClientPageStyles.flowInlineActionRow}>
      <FormInput
        name="tenderSearch"
        label={guaranteesClientText.prozorroSearchLabel}
        placeholder={guaranteesClientText.prozorroSearchPlaceholder}
        required
        disabled={disabled || isSearchingTender || isSelectingTender}
        error={!!tenderSearchError}
        helperText={tenderSearchError || undefined}
        onChange={handleTenderSearchChange}
        sx={guaranteesClientPageStyles.flowField}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
        }}
      />

      <Button
        type="button"
        variant="outlined"
        disabled={disabled || isSearchingTender || isSelectingTender}
        startIcon={isSearchingTender ? <ButtonLoadingIcon /> : undefined}
        onClick={handleTenderSearchClick}
        sx={guaranteesClientPageStyles.flowSecondaryAction}
      >
        {guaranteesClientText.prozorroSearchButton}
      </Button>
    </Box>
  );
};

export default TenderSearchRow;
